import React from 'react';
import { Printer } from 'lucide-react';
import { useDashboard } from '../../DashboardContext';
import { MACRS_RATES, SE_TAX, TAX_YEAR } from '../../lib/constants';
import { ReportCover, SectionHeader, NextStep } from '../shared/Report';

const Sec = ({ n, title }) => <SectionHeader number={n} kicker={`Section ${n.replace(/^0/, '')}`} title={title} />;

export default function CPAReportSection() {
  const {
    activeStrategy,
    activeCalc,
    miningEconomics,
    taxBenefits,
    taxStructureComparison,
    showAfterTaxEconomics,
    equivalentDCA,
    timeHorizon,
    btcPrice,
    electricityCost,
    hostingFixed,
    poolFee,
    startingTreasury,
    startingCash,
    taxRate,
    stateTaxRate,
    effectiveIncomeTaxRate,
    feieApplies,
    FEIE_EXCLUSION,
    useBonusDepreciation,
    entityType,
    filingStatus,
    ownerResidency,
    foreignTaxRegime,
    llcManagementLocation,
    incomeType,
    w2SSWages,
    otherSEIncome,
    scorpSalaryPercent,
    qbiEnabled,
    stateConformsBonus,
    operatorEconomics,
    advisorProfile,
    planMgr,
    fmt, fBTC,
  } = useDashboard();

  if (!activeCalc || !miningEconomics) {
    return (
      <div className="text-sm italic p-4" style={{ color: '#737373' }}>
        Configure a strategy and enable Tax Analysis (Tax & Economics tab) to generate a CPA-ready report.
      </div>
    );
  }

  if (!showAfterTaxEconomics || !taxStructureComparison) {
    return (
      <div style={{ padding: 24 }}>
        <h2 className="font-bold text-lg" style={{ color: '#1A1A1A' }}>CPA Report</h2>
        <div style={{ marginTop: 16, padding: '20px 24px', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8 }}>
          <p style={{ fontSize: 14, color: '#92400E', fontWeight: 500 }}>Tax analysis must be enabled to generate this report.</p>
          <p style={{ fontSize: 13, color: '#92400E', marginTop: 4 }}>Go to <strong>Tax & Economics</strong> and check "Enable tax analysis" first.</p>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const strat = activeStrategy;
  const annualData = activeCalc.annualData || [];
  const totalCapEx = miningEconomics.totalCapExPaid || miningEconomics.totalCapEx || 0;
  const totalCapExFull = miningEconomics.totalCapEx || 0;
  const totalOpEx = miningEconomics.totalOpEx || 0;
  const combinedRate = effectiveIncomeTaxRate + stateTaxRate;
  const yearlyDep = taxBenefits?.yearlyBreakdown || [];

  const totalMiners = strat.startingMiners + (strat.expansions || []).reduce((s, e) => s + (e.miners || 0), 0);

  const entityLabels = { llc: 'LLC (Pass-Through)', mmllc_foreign: 'Multi-Member LLC (Foreign)', scorp: 'S-Corporation', ccorp: 'C-Corporation' };
  const residencyLabels = { us_resident: 'US Resident', us_citizen_abroad: 'US Citizen Abroad', nra_treaty: 'NRA (Treaty Country)', nra_no_treaty: 'NRA (No Treaty)' };
  const filingLabels = { single: 'Single / Head of Household', mfj: 'Married Filing Jointly' };

  // ── Styles ──
  // Page-break behavior comes from the .print-block / .print-flow classes in
  // globals.css (@media print) — atomic sections get .print-block, sections
  // with large multi-page tables are left free to flow.
  const section = { marginBottom: 28 };
  const subTitle = { fontSize: 12, fontWeight: 600, color: '#333333', marginBottom: 8, marginTop: 16 };
  const row = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #F5F5F5' };
  const label = { fontSize: 12, color: '#525252' };
  const value = { fontSize: 12, fontWeight: 600, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' };
  const greenVal = { ...value, color: '#16A34A' };
  const redVal = { ...value, color: '#DC2626' };
  const note = { fontSize: 10, color: '#737373', lineHeight: 1.6 };
  const thStyle = { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #E5E5E5', color: '#525252', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #F5F5F5', fontSize: 11, fontVariantNumeric: 'tabular-nums' };
  const tdRight = { ...tdStyle, textAlign: 'right' };
  const thRight = { ...thStyle, textAlign: 'right' };

  // Quarterly estimated payments
  const annualTaxLiability = (taxStructureComparison.entityComparison?.[entityType === 'mmllc_foreign' ? 'llc' : entityType]?.total || 0);
  const quarterlyEstimate = annualTaxLiability > 0 ? annualTaxLiability / 4 : 0;

  // Annual OpEx breakdown — averaged from the simulation so it matches the
  // Section 4 projections table exactly (includes uptime + per-expansion specs)
  const nYears = Math.max(1, annualData.length);
  const annualElectricity = annualData.reduce((s, y) => s + (y.electricityCost || 0), 0) / nYears;
  const annualHosting = annualData.reduce((s, y) => s + (y.hostingCost || 0), 0) / nYears;
  const annualPoolFees = annualData.reduce((s, y) => s + (y.poolFees || 0), 0) / nYears;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between no-print">
        <div>
          <h2 className="font-bold text-lg" style={{ color: '#1A1A1A' }}>CPA Report</h2>
          <p className="text-sm" style={{ color: '#737373' }}>Tax workpapers for your CPA or tax attorney.</p>
        </div>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-5 py-2.5 rounded font-medium text-sm transition-all"
          style={{ background: '#1A1A1A', color: '#FFFFFF', border: 'none', cursor: 'pointer' }}
        >
          <Printer className="w-4 h-4" />
          Print for CPA
        </button>
      </div>

      <div className="print-deliverable" style={{ background: '#FFFFFF', borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E5E5' }}>
        <ReportCover
          reportKicker="CPA workpapers"
          reportTitle="Bitcoin mining tax & depreciation report"
          subtitle={`Entity position, depreciation schedule, income projections, and estimated payments for CPA review. Tax year ${TAX_YEAR}+.`}
          preparedFor={planMgr?.clients?.find(c => c.id === planMgr.activeClientId)?.display_name || 'CPA / Tax Advisor Review'}
          advisor={advisorProfile}
          date={today}
        />

        <div style={{ padding: '28px 40px' }}>
          {/* ═══════ 1. ENTITY & TAXPAYER SUMMARY ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="01" title="Entity & taxpayer summary" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                {[
                  ['Entity Type', entityLabels[entityType] || entityType],
                  ['Filing Status', filingLabels[filingStatus]],
                  ['Federal Marginal Rate', feieApplies ? `${effectiveIncomeTaxRate.toFixed(1).replace(/\.0$/, '')}% (FEIE, bracket ${taxRate}%)` : `${taxRate}%`],
                  ['State Tax Rate', `${stateTaxRate}%`],
                  ['Combined Effective Rate', `${combinedRate.toFixed(1).replace(/\.0$/, '')}%`],
                  ['Tax Residency', residencyLabels[ownerResidency]],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
              <div>
                {[
                  ['W-2 Salary', fmt(w2SSWages)],
                  ['Other Business Income', fmt(otherSEIncome)],
                  ['QBI Deduction (199A)', qbiEnabled ? 'Eligible' : 'Not Applied'],
                  ['Bonus Depreciation', useBonusDepreciation ? '100% (OBBBA 2025)' : 'MACRS 5-Year'],
                  ['State Conforms to Bonus', stateConformsBonus ? 'Yes' : 'No'],
                  ...(entityType === 'scorp' ? [['S-Corp Salary %', `${scorpSalaryPercent}%`]] : []),
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
            </div>
            {(ownerResidency === 'us_citizen_abroad' || ownerResidency.startsWith('nra')) && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED' }}>International Tax Note</div>
                <div style={{ ...note, color: '#7C3AED' }}>
                  LLC managed from: {llcManagementLocation.toUpperCase()} | Foreign regime: {foreignTaxRegime.replace(/_/g, ' ')} | Income type: {incomeType.replace(/_/g, ' ')}
                  {feieApplies && ` | FEIE exclusion: ${fmt(FEIE_EXCLUSION)} (IRC §911); income tax rate adjusted to 0%, SE tax still applies`}
                </div>
              </div>
            )}
          </div>

          {/* ═══════ 2. MINING OPERATION OVERVIEW ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="02" title="Mining operation overview" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <div style={subTitle}>Fleet</div>
                {[
                  ['Starting Miners', `${strat.startingMiners} units`],
                  ['Total Miners (incl. expansions)', `${totalMiners} units`],
                  ['Hash Rate per Miner', `${strat.hashRate} TH/s`],
                  ['Power Draw per Miner', `${strat.powerDraw}W`],
                  ['Miner Lifespan', `${strat.initialMinerLifespan} months`],
                  ['Resale Value', fmt(strat.initialResaleValue)],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
              <div>
                <div style={subTitle}>Capital</div>
                {[
                  ['Total CapEx (all equipment)', fmt(totalCapExFull)],
                  ['CapEx Paid (excl. sunk)', fmt(totalCapEx)],
                  ['Starting Cash Reserve', fmt(startingCash)],
                  ['Starting BTC Treasury', `${fBTC(startingTreasury, 4)} BTC`],
                  ['BTC Price at Start', fmt(btcPrice)],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
              <div>
                <div style={subTitle}>Operating Costs</div>
                {[
                  ['Electricity Rate', `$${electricityCost}/kWh`],
                  ['Fixed Hosting', `$${hostingFixed}/miner/mo`],
                  ['Pool Fee', `${(poolFee * 100).toFixed(1)}%`],
                  ['Projection Horizon', `${timeHorizon} years`],
                  ['BTC Sell Policy', strat.sellPercent > 0 ? `${(strat.sellPercent * 100).toFixed(0)}%` : 'HODL 100%'],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
            </div>

            {/* Financing */}
            {(strat.useInitialFinancing || (strat.expansions || []).some(e => e.useLoan)) && (
              <div style={{ marginTop: 16 }}>
                <div style={subTitle}>Financing / Debt</div>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Purpose', 'Amount', 'Rate', 'Term', 'Type', 'Annual Interest'].map(h => (
                        <th key={h} style={h === 'Purpose' ? thStyle : thRight}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {strat.useInitialFinancing && (
                      <tr>
                        <td style={tdStyle}>Initial Fleet ({strat.startingMiners} miners)</td>
                        <td style={tdRight}>{fmt(strat.initialLoanAmount)}</td>
                        <td style={tdRight}>{(strat.initialLoanRate * 100).toFixed(1)}%</td>
                        <td style={tdRight}>{strat.initialLoanTerm} mo</td>
                        <td style={tdRight}>{strat.initialInterestOnly ? 'Interest Only' : 'Amortizing'}</td>
                        <td style={tdRight}>{fmt(strat.initialLoanAmount * strat.initialLoanRate)}</td>
                      </tr>
                    )}
                    {(strat.expansions || []).filter(e => e.useLoan).map((exp, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>Expansion Mo {exp.month} (+{exp.miners} miners)</td>
                        <td style={tdRight}>{fmt(exp.loanAmount)}</td>
                        <td style={tdRight}>{(exp.loanRate * 100).toFixed(1)}%</td>
                        <td style={tdRight}>{exp.loanTerm} mo</td>
                        <td style={tdRight}>{exp.interestOnly ? 'Interest Only' : 'Amortizing'}</td>
                        <td style={tdRight}>{fmt(exp.loanAmount * exp.loanRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ ...note, marginTop: 4 }}>Loan interest is deductible as a business expense. Principal payments are not deductible.</div>
              </div>
            )}
          </div>

          {/* ═══════ 3. DEPRECIATION SCHEDULE ═══════ */}
          {/* No .print-block here — the depreciation table may exceed one page and must flow */}
          <div style={section}>
            <Sec n="03" title={`Depreciation schedule: ${useBonusDepreciation ? '100% bonus (OBBBA 2025, IRC 168(k))' : 'MACRS 5-year (GDS)'}`} />

            {useBonusDepreciation ? (
              <div>
                <div className="print-block" style={{ padding: '12px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 4, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#16A34A' }}>100% First-Year Expensing</div>
                  <div style={note}>
                    Under the One Big Beautiful Bill Act (OBBBA 2025), mining equipment placed in service after January 19, 2025 qualifies for 100% bonus depreciation under IRC 168(k).
                    The full cost basis is deductible in the year placed in service. No depreciation recapture applies unless equipment is disposed of.
                  </div>
                </div>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Tax Year', 'CapEx Placed in Service', 'Bonus Dep (100%)', 'Tax Savings', 'Cumulative Savings'].map(h => (
                        <th key={h} style={h === 'Tax Year' ? thStyle : thRight}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyDep.map((yr, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{yr.year}</td>
                        <td style={tdRight}>{fmt(yr.depreciation)}</td>
                        <td style={tdRight}>{fmt(yr.depreciation)}</td>
                        <td style={{ ...tdRight, color: '#16A34A', fontWeight: 600 }}>{fmt(yr.taxSavings)}</td>
                        <td style={tdRight}>{fmt(yr.cumulative)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #E5E5E5', fontWeight: 700 }}>
                      <td style={tdStyle}>Total</td>
                      <td style={tdRight}>{fmt(totalCapEx)}</td>
                      <td style={tdRight}>{fmt(totalCapEx)}</td>
                      <td style={{ ...tdRight, color: '#16A34A' }}>{fmt(taxBenefits?.totalTaxSavings || 0)}</td>
                      <td style={tdRight}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div>
                <div className="print-block" style={{ padding: '12px 16px', background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: 4, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#333333' }}>5-Year MACRS (GDS) Schedule</div>
                  <div style={note}>
                    Mining equipment classified as 5-year property under MACRS. Using 200% declining balance switching to straight-line.
                    Convention: Half-year. Rates: {MACRS_RATES.map((r, i) => `Yr${i + 1}: ${(r * 100).toFixed(1)}%`).join(', ')}
                  </div>
                </div>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Tax Year', 'Depreciation', 'Tax Savings', 'Cumulative'].map(h => (
                        <th key={h} style={h === 'Tax Year' ? thStyle : thRight}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyDep.map((yr, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{yr.year}</td>
                        <td style={tdRight}>{fmt(yr.depreciation)}</td>
                        <td style={{ ...tdRight, color: '#16A34A', fontWeight: 600 }}>{fmt(yr.taxSavings)}</td>
                        <td style={tdRight}>{fmt(yr.cumulative)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!stateConformsBonus && useBonusDepreciation && stateTaxRate > 0 && (
              <div className="print-block" style={{ marginTop: 8, padding: '8px 14px', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>State Non-Conformity Adjustment</div>
                <div style={{ ...note, color: '#92400E' }}>
                  Your state does not conform to federal bonus depreciation. State depreciation follows MACRS 5-year schedule.
                  Adjustment: {fmt(taxStructureComparison.stateNonConformityAdjustment)} reduction in state tax savings.
                </div>
              </div>
            )}
          </div>

          {/* ═══════ 4. ANNUAL INCOME & EXPENSE PROJECTIONS ═══════ */}
          {/* No .print-block here — the annual projections table may exceed one page and must flow */}
          <div style={section}>
            <Sec n="04" title="Annual income & expense projections" />
            <div className="print-flow" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                  <tr>
                    {['Year', 'BTC Mined', 'Gross Revenue', 'Electricity', 'Hosting', 'Pool Fees', 'Total OpEx', 'Loan Pmts', 'Cash Flow', 'Miners EOY'].map(h => (
                      <th key={h} style={h === 'Year' ? thStyle : thRight}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {annualData.map((yr, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{yr.year}</td>
                      <td style={tdRight}>{fBTC(yr.btcMined, 4)}</td>
                      <td style={tdRight}>{fmt(yr.revenue)}</td>
                      <td style={tdRight}>{fmt(yr.electricityCost || 0)}</td>
                      <td style={tdRight}>{fmt(yr.hostingCost || 0)}</td>
                      <td style={tdRight}>{fmt(yr.poolFees || 0)}</td>
                      <td style={tdRight}>{fmt(yr.opEx)}</td>
                      <td style={tdRight}>{fmt(yr.loanPayments)}</td>
                      <td style={{ ...tdRight, color: yr.netProfit >= 0 ? '#16A34A' : '#DC2626', fontWeight: 600 }}>{fmt(yr.netProfit)}</td>
                      <td style={tdRight}>{yr.minersEOY}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E5E5', fontWeight: 700 }}>
                    <td style={tdStyle}>Total</td>
                    <td style={tdRight}>{fBTC(annualData.reduce((s, y) => s + y.btcMined, 0), 4)}</td>
                    <td style={tdRight}>{fmt(annualData.reduce((s, y) => s + y.revenue, 0))}</td>
                    <td style={tdRight}>{fmt(annualData.reduce((s, y) => s + (y.electricityCost || 0), 0))}</td>
                    <td style={tdRight}>{fmt(annualData.reduce((s, y) => s + (y.hostingCost || 0), 0))}</td>
                    <td style={tdRight}>{fmt(annualData.reduce((s, y) => s + (y.poolFees || 0), 0))}</td>
                    <td style={tdRight}>{fmt(annualData.reduce((s, y) => s + y.opEx, 0))}</td>
                    <td style={tdRight}>{fmt(annualData.reduce((s, y) => s + y.loanPayments, 0))}</td>
                    <td style={{ ...tdRight, color: '#1A1A1A' }}>{fmt(annualData.reduce((s, y) => s + y.netProfit, 0))}</td>
                    <td style={tdRight}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{ ...note, marginTop: 6 }}>
              Revenue = Fair market value of BTC sold. Cash Flow = operating cash flow from BTC sales + other income − OpEx + resale proceeds (excludes CapEx and depreciation).
              OpEx includes electricity, hosting, and pool fees. All amounts projected.
            </div>
          </div>

          {/* ═══════ 5. TAX DEDUCTIONS SUMMARY ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="05" title="Tax deductions summary" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <div style={subTitle}>Deductible Expenses (Annual Average)</div>
                {[
                  ['Electricity (metered power)', fmt(annualElectricity)],
                  ['Hosting / Colocation', fmt(annualHosting)],
                  ['Mining Pool Fees', fmt(annualPoolFees)],
                  ['Loan Interest (if applicable)', 'See financing table'],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
                <div style={{ ...row, borderTop: '2px solid #E5E5E5', marginTop: 4 }}>
                  <span style={{ ...label, fontWeight: 600 }}>Total Annual OpEx Deduction</span>
                  <span style={greenVal}>{fmt(annualElectricity + annualHosting + annualPoolFees)}</span>
                </div>
              </div>
              <div>
                <div style={subTitle}>Tax Savings Breakdown ({timeHorizon}-Year Total)</div>
                {[
                  ['Depreciation Tax Savings', fmt(taxStructureComparison.depreciationTaxSavings), '#16A34A'],
                  ['OpEx Tax Deductions', fmt(taxStructureComparison.opExTaxSavings), '#16A34A'],
                  ...(taxStructureComparison.qbiTaxSavings > 0 ? [['QBI Deduction (199A)', fmt(taxStructureComparison.qbiTaxSavings), '#16A34A']] : []),
                  ['Mining SE / Payroll Tax', `-${fmt(taxStructureComparison.totalMiningSETax)}`, '#DC2626'],
                ].map(([l, v, color], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={{ ...value, color }}>{v}</span></div>
                ))}
                <div style={{ ...row, borderTop: '2px solid #E5E5E5', marginTop: 4 }}>
                  <span style={{ ...label, fontWeight: 600 }}>Net Mining Tax Benefit</span>
                  <span style={greenVal}>{fmt(taxStructureComparison.totalMiningTaxBenefit)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════ 6. ENTITY STRUCTURE COMPARISON ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="06" title="Entity structure tax comparison (annual)" />
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['', 'LLC / Sole Prop', 'S-Corporation', 'C-Corporation'].map(h => (
                    <th key={h} style={h === '' ? thStyle : thRight}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const ec = taxStructureComparison.entityComparison;
                  const rows = [
                    ['Mining Net Income', fmt(operatorEconomics?.miningNetProfit || 0), fmt(operatorEconomics?.miningNetProfit || 0), fmt(operatorEconomics?.miningNetProfit || 0)],
                    ['Income Tax', fmt(ec.llc.incomeTax), fmt(ec.scorp.incomeTax), `${fmt(ec.ccorp.corpTax)} (21% flat)`],
                    ['SE Tax / Payroll', fmt(ec.llc.seTax), fmt(ec.scorp.payrollTax), '$0'],
                    ['QBI Savings', qbiEnabled ? `-${fmt(ec.llc.qbiSavings)}` : 'N/A', qbiEnabled ? `-${fmt(ec.scorp.qbiSavings)}` : 'N/A', 'N/A'],
                  ];
                  rows.push(['S-Corp W-2 Salary', '—', fmt(ec.scorp.salary), '—']);
                  rows.push(['S-Corp K-1 Distribution', '—', fmt(ec.scorp.distribution), '—']);
                  rows.push(['Total Annual Tax', fmt(ec.llc.total), fmt(ec.scorp.total), fmt(ec.ccorp.total)]);
                  rows.push(['Savings vs LLC', '—', ec.scorp.savingsVsLLC > 0 ? fmt(ec.scorp.savingsVsLLC) : '—', ec.llc.total > ec.ccorp.total ? fmt(ec.llc.total - ec.ccorp.total) : '—']);

                  return rows.map(([label, llc, scorp, ccorp], i) => {
                    const isTotal = label === 'Total Annual Tax';
                    const isSavings = label === 'Savings vs LLC';
                    return (
                      <tr key={i} style={isTotal ? { borderTop: '2px solid #E5E5E5', fontWeight: 700 } : {}}>
                        <td style={tdStyle}>{label}</td>
                        <td style={{ ...tdRight, ...(isSavings ? { color: '#16A34A' } : {}), ...(isTotal ? { fontWeight: 700 } : {}) }}>{llc}</td>
                        <td style={{ ...tdRight, ...(isSavings ? { color: '#16A34A', fontWeight: 700 } : {}), ...(isTotal ? { fontWeight: 700 } : {}) }}>{scorp}</td>
                        <td style={{ ...tdRight, ...(isSavings ? { color: '#16A34A' } : {}), ...(isTotal ? { fontWeight: 700 } : {}) }}>{ccorp}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            <div style={{ ...note, marginTop: 6 }}>
              S-Corp: Salary set at {scorpSalaryPercent}% of net income. IRS requires "reasonable compensation", typically 25-40%.
              C-Corp: 21% flat rate, no SE tax. Dividends taxed separately at shareholder level (not shown).
              QBI (199A): 20% deduction on qualified pass-through income. Subject to W-2 wage/UBIA limitations at higher income levels.
            </div>
          </div>

          {/* ═══════ 7. ESTIMATED QUARTERLY PAYMENTS ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="07" title="Estimated tax payments (Form 1040-ES / 1120-W)" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <div style={subTitle}>Quarterly Estimated Payments</div>
                {[
                  ['Annual Mining Tax Liability', fmt(annualTaxLiability)],
                  ['Q1 (Apr 15)', fmt(quarterlyEstimate)],
                  ['Q2 (Jun 15)', fmt(quarterlyEstimate)],
                  ['Q3 (Sep 15)', fmt(quarterlyEstimate)],
                  ['Q4 (Jan 15 next year)', fmt(quarterlyEstimate)],
                ].map(([l, v], i) => (
                  <div key={i} style={row}>
                    <span style={label}>{l}</span>
                    <span style={i === 0 ? redVal : value}>{v}</span>
                  </div>
                ))}
                <div style={{ ...note, marginTop: 6 }}>
                  Based on current entity ({entityLabels[entityType] || entityType}) and {combinedRate}% combined rate.
                  Safe harbor: Pay 100% of prior year tax (110% if AGI &gt; $150K) to avoid underpayment penalty.
                </div>
              </div>
              <div>
                <div style={subTitle}>Key Tax Dates</div>
                {[
                  ['Jan 15', 'Q4 estimated payment due'],
                  ['Mar 15', 'S-Corp / Partnership return due (Form 1120-S / 1065)'],
                  ['Apr 15', 'Q1 estimated payment + Individual return (1040) due'],
                  ['Jun 15', 'Q2 estimated payment due'],
                  ['Sep 15', 'Q3 estimated payment + S-Corp/Partnership extension due'],
                  ['Oct 15', 'Individual extension deadline'],
                ].map(([date, desc], i) => (
                  <div key={i} style={{ ...row, alignItems: 'flex-start' }}>
                    <span style={{ ...value, minWidth: 50 }}>{date}</span>
                    <span style={{ ...label, textAlign: 'right' }}>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ═══════ 8. SE TAX ANALYSIS ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="08" title="Self-employment tax analysis" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <div style={subTitle}>Mining SE Tax</div>
                {[
                  ['Annual Mining SE Tax', fmt(taxStructureComparison.annualMiningSETax)],
                  [`Total SE Tax (${timeHorizon} years)`, fmt(taxStructureComparison.totalMiningSETax)],
                  ['SE Tax Exempt?', taxStructureComparison.seTaxExemptReason || 'No'],
                  ...(taxStructureComparison.totalMiningNIIT > 0 ? [['NIIT (3.8%, passive, §1411)', fmt(taxStructureComparison.annualMiningNIIT) + '/yr']] : []),
                  ['Activity Type', taxStructureComparison.isPassive ? 'Passive' : 'Active / Material Participation'],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
              <div>
                <div style={subTitle}>Other SE Income Tax</div>
                {[
                  ['Other Business Income', fmt(otherSEIncome)],
                  ['Annual SE Tax (other)', fmt(taxStructureComparison.annualOtherSETax)],
                  [`SS Wage Base (${TAX_YEAR})`, `$${SE_TAX.ssWageBase.toLocaleString()}`],
                  ['SS Rate', `${(SE_TAX.ssRate * 100).toFixed(1)}% (up to base)`],
                  ['Medicare Rate', `${(SE_TAX.medicareRate * 100).toFixed(1)}% (uncapped)`],
                ].map(([l, v], i) => (
                  <div key={i} style={row}><span style={label}>{l}</span><span style={value}>{v}</span></div>
                ))}
              </div>
            </div>
            {taxStructureComparison.eblApplies && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#DC2626' }}>Excess Business Loss (EBL) Limitation, IRC 461(l)</div>
                <div style={{ ...note, color: '#DC2626' }}>
                  Business losses exceeding {fmt(taxStructureComparison.eblLimit)} ({filingLabels[filingStatus]}) are limited.
                  Excess: {fmt(taxStructureComparison.eblExcess)} carries forward as a net operating loss (NOL).
                  This may impact year-one deductions if bonus depreciation creates a large loss.
                </div>
              </div>
            )}
            {taxStructureComparison.passiveActivityWarning && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#92400E' }}>Passive Activity Loss Rules, IRC 469</div>
                <div style={{ ...note, color: '#92400E' }}>
                  If mining is classified as passive (no material participation), losses including depreciation may only offset passive income.
                  Ensure material participation (500+ hours/year or substantially all participation) to deduct against active income.
                </div>
              </div>
            )}
          </div>

          {/* ═══════ 9. MINING vs DCA COST COMPARISON ═══════ */}
          {equivalentDCA && (
            <div className="print-block" style={section}>
              <Sec n="09" title="Mining vs DCA: cost basis comparison" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div style={{ padding: 16, background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#92700C', textTransform: 'uppercase' }}>Mining</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#92700C', marginTop: 6 }}>{fBTC(equivalentDCA.miningTreasury, 4)} BTC</div>
                  <div style={{ fontSize: 11, color: '#525252', marginTop: 2 }}>Gross Cost/BTC: {fmt(equivalentDCA.miningCostPerBTC)}</div>
                  {equivalentDCA.totalTaxSavings > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#16A34A', marginTop: 2 }}>After-Tax Cost/BTC: {fmt(equivalentDCA.miningAfterTaxCostPerBTC)}</div>
                  )}
                </div>
                <div style={{ padding: 16, background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#2563EB', textTransform: 'uppercase' }}>DCA Bitcoin (same $)</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#2563EB', marginTop: 6 }}>{fBTC(equivalentDCA.dcaBtcAccum, 4)} BTC</div>
                  <div style={{ fontSize: 11, color: '#525252', marginTop: 2 }}>Cost/BTC: {fmt(equivalentDCA.dcaCostPerBTC)}</div>
                  <div style={{ fontSize: 10, color: '#DC2626', marginTop: 2 }}>No tax deductions available</div>
                </div>
                <div style={{ padding: 16, background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase' }}>Tax Advantage</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#16A34A', marginTop: 6 }}>{fmt(equivalentDCA.totalTaxSavings)}</div>
                  <div style={{ fontSize: 11, color: '#525252', marginTop: 2 }}>
                    {equivalentDCA.miningGrossCost > 0 ? `${((equivalentDCA.totalTaxSavings / equivalentDCA.miningGrossCost) * 100).toFixed(0)}%` : '0%'} cost reduction
                  </div>
                  <div style={{ fontSize: 10, color: '#525252', marginTop: 2 }}>vs buying same BTC at spot</div>
                </div>
              </div>
              <div style={{ ...note, marginTop: 8 }}>
                Mining hardware and operating expenses are deductible business costs. DCA purchases are made with after-tax dollars and receive no deductions.
                This comparison shows the tax-advantaged cost basis of mining vs direct market purchases of the same amount of BTC.
              </div>
            </div>
          )}

          {/* ═══════ 10. CPA ACTION ITEMS ═══════ */}
          <div className="print-block" style={section}>
            <Sec n="10" title="Action items for tax advisor" />
            <div style={{ padding: '16px 20px', background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: 4 }}>
              <ol style={{ fontSize: 12, color: '#333333', lineHeight: 2, margin: 0, paddingLeft: 20 }}>
                <li><strong>Verify entity classification</strong>. Confirm {entityLabels[entityType] || entityType} is properly elected and compliant. {entityType === 'scorp' ? 'Ensure Form 2553 is filed.' : ''}</li>
                <li><strong>Confirm bonus depreciation eligibility</strong>. OBBBA 2025 restored 100% for equipment placed in service after 1/19/25. Verify qualifying property classification.</li>
                <li><strong>Establish cost basis</strong>. Document miner purchase price, date placed in service, and expected useful life for each cohort.</li>
                <li><strong>Track BTC fair market value at receipt</strong>. Per IRS Notice 2014-21 / Rev. Rul. 2023-14, mined BTC is ordinary income at FMV on date received.</li>
                <li><strong>Document material participation</strong>. Maintain contemporaneous logs of mining activity hours to satisfy IRC 469 material participation tests.</li>
                <li><strong>Set up estimated payments</strong>. {quarterlyEstimate > 0 ? `Approximately ${fmt(quarterlyEstimate)}/quarter based on projected mining income.` : 'Determine quarterly amounts once mining revenue begins.'}</li>
                <li><strong>Review state tax nexus</strong>. Mining equipment location and management activities may create nexus in multiple states.</li>
                {entityType === 'scorp' && (
                  <li><strong>Document reasonable compensation</strong>. S-Corp officer salary at {scorpSalaryPercent}% of net income. Prepare comparable salary analysis.</li>
                )}
                {taxStructureComparison.eblApplies && (
                  <li><strong>EBL limitation planning</strong>. Year-one losses may exceed {fmt(taxStructureComparison.eblLimit)} limit. Plan for NOL carryforward.</li>
                )}
                <li><strong>Intercompany funding structure</strong>. If another entity funds this LLC's operations, document whether transfers are capital contributions, intercompany loans, or management fees; each changes who takes deductions and member basis under §704(d)/§465.</li>
                <li><strong>Startup costs (§195)</strong>. For a newly formed LLC, up to $5,000 of startup costs are deductible in year one (phase-out above $50K), remainder amortized over 180 months.</li>
                <li><strong>Maintain business records</strong>. Electricity bills, hosting invoices, pool fee statements, wallet addresses, and blockchain transaction records.</li>
                {ownerResidency !== 'us_resident' && (
                  <li><strong>International filing requirements</strong>. Review FBAR, Form 8938, Form 5471/8865 obligations based on entity structure and foreign management.</li>
                )}
              </ol>
            </div>
          </div>

          <NextStep
            advisor={advisorProfile}
            disclaimer="Informational only; not tax, legal, or financial advice. Projections rest on assumed growth rates, current tax law, and estimated operating costs, and actual results will vary. Verify entity elections, depreciation methods, and IRS classification of the mining activity with a qualified CPA or tax attorney. This document is a starting point for that conversation."
          />
        </div>
      </div>
    </div>
  );
}
